import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Heart,
  ShoppingCart,
  Truck,
  Shield,
  ChevronLeft,
  ChevronRight,
  Star,
  Plus,
  Minus,
  Check,
  Share2,
  Facebook,
  Twitter,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserLikes, toggleLike } from "../../redux/likesSlice";
import { shuffle } from "lodash";
import { FaWhatsapp, FaTelegram } from "react-icons/fa";

import Reviews from "./Reviews";
import { usePanier } from "../homePage/PanierContext";
import ProductAvailabilityBadge from "./ProductAvailabilityBadge";
// Récupération des couleurs du thème
const COLORS = {
  orange: "#E87E04",
  white: "#FFFFFF",
  green: "#008751",
  darkGreen: "#006B3F",
  lightOrange: "#FFE0B2",
  lightGreen: "#CCFFCC",
  gray: "#718096",
  lightGray: "#E2E8F0",
  paleGreen: "#E6FFEA",
};
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const ProductDetailPage = ({ product, products }) => {
  // État pour les images en utilisant les images du produit
  const [images, setImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [availableSizes, setAvailableSizes] = useState([]);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationType, setNotificationType] = useState("success");
  const [produitsL, setProduitsL] = useState(0);
  const [rates, setRate] = useState(0);
  const [countRates, setCountRate] = useState(0);
  const DATA_Categories = useSelector((state) => state.products.categories);
  const navigate = useNavigate();
  const { updatePanier, panier } = usePanier();

  // Initialisation des images et de la première variante
  useEffect(() => {
    if (product) {
      // Collecter toutes les images (principale + celles des variantes)
      const allImages = [
        product?.image1,
        product?.image2,
        product?.image3,
        ...product.variants.filter((v) => v.imageUrl).map((v) => v.imageUrl),
      ].filter(Boolean); // Filtre les valeurs undefined ou null

      setImages(allImages);

      // Sélectionner la première variante disponible
      if (product.variants && product.variants.length > 0) {
        const firstVariant = product.variants[0];
        setSelectedColor(firstVariant.color);
        setSelectedVariant(firstVariant);

        // Définir les tailles disponibles pour cette variante
        if (firstVariant.sizes && firstVariant.sizes.length > 0) {
          setAvailableSizes(firstVariant.sizes);
          setSelectedSize(firstVariant.sizes[0]);
        }
      }
    }
  }, [product]);
  function extractTextFromHTML(htmlString) {
    // Crée un élément temporaire pour parser le HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;

    // Récupère uniquement le texte lisible
    return tempDiv.innerText.trim();
  }
  const handleAdd = (produit) => {
    const nouveauPanier = [...panier, produit];
    updatePanier(produit);
  };

  const dispatch = useDispatch();
  const likedProducts = useSelector((state) => state.likes.likedProducts);
  const userId = JSON.parse(localStorage.getItem("userEcomme"))?.id;
  const userName = JSON.parse(localStorage.getItem("userEcomme"))?.name;

  useEffect(() => {
    if (userId) {
      dispatch(fetchUserLikes(userId));
    }
  }, [userId, dispatch]);

  const showToast = (message, type = "success") => {
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  // Gestion des images (suivante/précédente)
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  // Sélection d'une variante de couleur
  const handleColorSelect = (color) => {
    const variant = product.variants.find((v) => v.color === color);
    if (variant) {
      setSelectedColor(color);
      setSelectedVariant(variant);
      setAvailableSizes(variant.sizes || []);
      setSelectedSize(
        variant.sizes && variant.sizes.length > 0 ? variant.sizes[0] : null
      );

      // Si cette variante a une image, la montrer
      if (variant.imageUrl) {
        const imageIndex = images.findIndex((img) => img === variant.imageUrl);
        if (imageIndex !== -1) {
          setCurrentImageIndex(imageIndex);
        }
      }
    }
  };

  // Sélection d'une taille
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
  };

  // Gestion de la quantité
  // const incrementQuantity = () => {
  //   if (selectedVariant && quantity < selectedVariant.stock) {
  //     setQuantity((prevQuantity) => prevQuantity + 1);
  //   }  
  // };
  const incrementQuantity = () => {
    if (selectedVariant || product?.variants?.length === 0) {
      // alert(`Stock disponible pour cette variante: ${selectedVariant?.stock}`);
      // alert(quantity);
      setQuantity((prevQuantity) => prevQuantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity((prevQuantity) => prevQuantity - 1);
    }
  };

  // Gestion de la saisie directe de la quantité
  const handleQuantityChange = (e) => {
    const value = e.target.value;
    
    // Permettre uniquement les chiffres
    if (value === '' || /^\d+$/.test(value)) {
      const numValue = value === '' ? 1 : parseInt(value, 10);
      
      // Limiter à un minimum de 1
      if (numValue >= 1) {
        setQuantity(numValue);
      } else {
        setQuantity(1);
      }
    }
  };

  // Valider la quantité quand on quitte le champ
  const handleQuantityBlur = () => {
    if (quantity < 1 || isNaN(quantity)) {
      setQuantity(1);
    }
  };

  // Calculer le prix et la remise
  const originalPrice = product?.prix;
  const discountedPrice = product?.prixPromo;
  const discountPercentage = Math.round(
    ((originalPrice - discountedPrice) / originalPrice) * 100
  );

  // Ajout au panier
  const addToCart = (parame) => {
    if (!product.isdisponible) {
      showToast(`Produit non disponible pour le momant!`, "error");
      return;
    }
    // Vérification des variantes de couleur
    if (product?.variants && product.variants.length >= 2 && !selectedVariant) {
      showToast(
        `Veuillez choisir un modèle parmi les ${product.variants.length}`,
        "error"
      );
      return;
    }

    // Vérification des tailles
    const hasMultipleSizes = product?.variants?.some(
      (variant) => variant.sizes && variant.sizes.length >= 2
    );

    if (hasMultipleSizes && !selectedSize) {
      showToast(`Veuillez choisir une taille parmi les disponibles`, "error");
      return;
    }

    // Récupérer les produits existants dans le panier
    const existingProducts = JSON.parse(localStorage.getItem("panier")) || [];

    // Vérifier si le produit existe déjà dans le panier
    const existingProductIndex = existingProducts.findIndex((p) => {
      // Si le produit n'a pas de variantes, on compare simplement l'ID
      if (!product.variants || product.variants.length === 0) {
        return p?._id === product?._id;
      }

      // Si le produit a des variantes, on compare l'ID, la couleur et la taille
      return (
        p?._id === product?._id &&
        p.colors[0] === selectedVariant?.color &&
        p.sizes[0] === selectedSize
      );
    });

    if (existingProductIndex !== -1) {
      // Produit existant : incrémenter la quantité
      const updatedProducts = existingProducts.map((p, index) =>
        index === existingProductIndex ? { ...p, quantity: p.quantity + 1 } : p
      );

      // localStorage.setItem("panier", JSON.stringify(updatedProducts));
      handleAdd(updatedProducts);
      showToast("La quantité du produit a été incrémentée dans le panier !");
    } else {
      // Nouveau produit à ajouter
      const newProduct = {
        ...product,
        colors: selectedVariant ? [selectedVariant.color] : [],
        sizes: selectedSize ? [selectedSize] : [],
        quantity: quantity,
        _id: product?._id,
        imageUrl: selectedVariant ? selectedVariant.imageUrl : product?.image1,
        price:
          discountedPrice && discountedPrice > 0
            ? discountedPrice
            : originalPrice,
        prixPromo: discountedPrice,
      };

      const updatedProducts = [...existingProducts, newProduct];
      // localStorage.setItem("panier", JSON.stringify(updatedProducts));
      handleAdd(updatedProducts);
      showToast("Produit ajouté au panier !");
    }

    // Mettre à jour la longueur du panier
    const local = localStorage.getItem("panier");
    // panierchg();
    if (local) {
      setProduitsL(JSON.parse(local));
    } else {
      setProduitsL(0);
    }
    if (parame) {
      navigate("/panier");
    }
  };

  // Basculer l'état du favori
  const toggleWishlist = () => {
    setIsWishlisted(!isWishlisted);
  };

  // Si pas de produit, montrer un loader
  if (!product) {
    return (
      <div className="flex justify-center items-center h-screen">
        Chargement...
      </div>
    );
  }

  // Calcul du pourcentage de réduction
  const calculateDiscount = (originalPrice, promoPrice) => {
    return Math.round(((originalPrice - promoPrice) / originalPrice) * 100);
  };

  const handleLikeClick = async (product, e) => {
    e.stopPropagation();

    if (!userId) {
      showToast("Veuillez vous connecter pour ajouter des favoris", "error");
      return;
    }

    try {
      await dispatch(toggleLike({ userId, product })).unwrap();
      showToast(
        likedProducts.includes(product._id)
          ? "Produit retiré des favoris"
          : "Produit ajouté aux favoris"
      );
    } catch (error) {
      showToast("Une erreur est survenue", "error");
      console.error("Erreur:", error);
    }
  };

  const ShareButtons = ({ product }) => {
    // URL actuelle de la page
    const currentUrl =
      typeof window !== "undefined" ? window.location.href : "";

    // Titre du produit à partager
    const shareTitle = `Découvrez ${product.name} de ${product.marque}`;

    // Configuration pour chaque plateforme
    const platforms = [
      {
        name: "facebook",
        icon: <Facebook className="h-4 w-4 text-blue-600" />,
        url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
          currentUrl
        )}`,
      },
      {
        name: "twitter",
        icon: <Twitter className="h-4 w-4 text-sky-500" />,
        url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
          currentUrl
        )}&text=${encodeURIComponent(shareTitle)}`,
      },
      {
        name: "whatsapp",
        icon: <FaWhatsapp className="h-4 w-4 text-green-500" />,
        url: `https://wa.me/?text=${encodeURIComponent(
          `${shareTitle} ${currentUrl}`
        )}`,
      },
      {
        name: "telegram",
        icon: <FaTelegram className="h-4 w-4 text-blue-400" />,
        url: `https://t.me/share/url?url=${encodeURIComponent(
          currentUrl
        )}&text=${encodeURIComponent(shareTitle)}`,
      },
    ];

    // Fonction pour ouvrir le lien de partage dans une nouvelle fenêtre
    const handleShare = (url) => {
      window.open(url, "_blank", "width=600,height=400");
    };

    return (
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">Partager:</span>
          <div className="flex gap-2">
            {platforms.map((platform) => (
              <button
                key={platform.name}
                onClick={() => handleShare(platform.url)}
                className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-all duration-200"
                aria-label={`Partager sur ${platform.name}`}
              >
                {platform.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Notification d'ajout au panier */}
      {showNotification && (
        <div
          className={cn(
            "fixed top-4 right-4 z-50 px-4 py-3 rounded shadow-lg transition-all duration-300",
            notificationType === "success"
              ? "bg-green-100 border-green-400 text-green-700"
              : "bg-red-100 border-red-400 text-red-700"
          )}
        >
          <p className="text-sm">{notificationMessage}</p>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Chemin de navigation */}
        <nav className="text-sm mb-6">
          <ol className="flex flex-wrap items-center">
            <li className="flex items-center">
              <a
                onClick={() => navigate(`/`)}
                className="text-gray-500 hover:text-orange-500"
              >
                Accueil
              </a>
              <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            </li>
            <li className="flex items-center">
              <a
                onClick={() => navigate(`/produits`)}
                className="text-gray-500 hover:text-orange-500"
              >
                Produits
              </a>
              <ChevronRight className="h-4 w-4 mx-2 text-gray-400" />
            </li>
            <li className="text-gray-900 font-medium truncate">
              {product.name}
            </li>
          </ol>
        </nav>

        {/* Section principale du produit */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Galerie d'images */}
          <div className="space-y-4">
            {/* Image principale */}
            <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-square">
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className="object-cover w-full h-full transform transition-transform duration-500"
              />

              {/* Badges promotion */}
              {/* {discountPercentage > 0 && (
                <div
                  className="absolute top-4 left-4 px-2 py-1 rounded text-white text-sm font-bold"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  -{discountPercentage}%
                </div>
              )} */}

              {(() => {
                // Détermine le prix normal (on prend d'abord le prix de la variante si disponible)
                const vari = selectedVariant;

                const normalPrice =
                  vari?.price && vari.price > 0
                    ? vari?.price
                    : product.prix || 0;

                const discountPrice =
                  vari?.promoPrice && vari.promoPrice > 0
                    ? vari?.promoPrice
                    : product.prixPromo || 0;

                // Détermine le prix promo (on prend d'abord le prix promo de la variante si disponible)
                // const discountPrice =
                //   variant.promoPrice || variant.prixPromo || 0;

                // Vérifie si le produit est en promotion
                const isOnPromo =
                  (discountPrice > 0 && discountPrice < normalPrice) ||
                  product.isOnPromo;
                const discountPercentage = Math.round(
                  ((normalPrice - discountPrice) / normalPrice) * 100
                );
                if (isOnPromo) {
                  return (
                    <>
                      <div
                        className="absolute top-4 left-4 px-2 py-1 rounded text-white text-sm font-bold"
                        style={{ backgroundColor: COLORS.orange }}
                      >
                        -{discountPercentage}%
                      </div>
                    </>
                  );
                } else {
                  return;
                }
              })()}

              {/* Boutons navigation des images */}
              <button
                onClick={handlePrevImage}
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-md"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={handleNextImage}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white/80 hover:bg-white shadow-md"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Miniatures */}
            <div className="grid grid-cols-5 gap-2">
              {images.map((image, index) => (
                <button
                  key={index}
                  className={`aspect-square rounded-md overflow-hidden ${
                    currentImageIndex === index
                      ? "ring-2 ring-offset-1"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  style={{
                    ringColor:
                      currentImageIndex === index
                        ? COLORS.orange
                        : "transparent",
                  }}
                  onClick={() => setCurrentImageIndex(index)}
                >
                  <img
                    src={image}
                    alt={`${product.name} - image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Informations du produit */}
          <div className="space-y-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {product.name}
                </h1>

                <button
                  onClick={(e) => handleLikeClick(product, e)}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <Heart
                    className={`h-6 w-6 ${
                      likedProducts.includes(product._id)
                        ? "fill-red-500 text-red-500"
                        : "text-gray-400"
                    }`}
                  />
                </button>
              </div>
              <div className="mt-2">
                <ProductAvailabilityBadge isAvailable={product.isdisponible} />
              </div>

              <div className="flex items-center">
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className="h-4 w-4"
                      style={{
                        color:
                          star <= countRates ? COLORS.orange : COLORS.lightGray,
                      }}
                      fill={star <= countRates ? COLORS.orange : "none"}
                    />
                  ))}
                </div>
                <span className="ml-2 text-sm text-gray-500">
                  ({countRates} avis)
                </span>
                <span className="mx-2 text-gray-300">|</span>
                <span
                  className="text-sm font-medium"
                  style={{ color: COLORS.green }}
                >
                  {product.quantite > 0 ? "En stock" : "Rupture de stock"}
                </span>
              </div>

              <div className="flex items-center space-x-2">
                {/* <span
                  className="text-2xl font-bold"
                  style={{ color: COLORS.darkGreen }}
                >
                  {(product.prixPromo || product.prix).toLocaleString()} €
                </span>
                {product.prixPromo && (
                  <span className="text-lg line-through text-gray-400">
                    {product.prix.toLocaleString()} €
                  </span>
                )} */}

                {(() => {
                  // Détermine le prix normal (on prend d'abord le prix de la variante si disponible)
                  const vari = selectedVariant;

                  const normalPrice =
                    vari?.price && vari.price > 0
                      ? vari?.price
                      : product.prix || 0;

                  const discountPrice =
                    vari?.promoPrice && vari.promoPrice > 0
                      ? vari?.promoPrice
                      : product.prixPromo || 0;

                  // Détermine le prix promo (on prend d'abord le prix promo de la variante si disponible)
                  // const discountPrice =
                  //   variant.promoPrice || variant.prixPromo || 0;

                  // Vérifie si le produit est en promotion
                  const isOnPromo =
                    (discountPrice > 0 && discountPrice < normalPrice) ||
                    product.isOnPromo;

                  if (isOnPromo) {
                    return (
                      <>
                        <span
                          className="text-2xl font-bold"
                          style={{ color: COLORS.darkGreen }}
                        >
                          {discountPrice} €
                        </span>
                        <span className="text-lg line-through text-gray-400">
                          {normalPrice} €
                        </span>
                      </>
                    );
                  } else {
                    return (
                      <div className="text-[#008751] font-medium ml-3">
                        {normalPrice} €
                      </div>
                    );
                  }
                })()}
              </div>

              <div>
                <span className="text-sm text-gray-500">Marque: </span>
                <span className="text-sm font-medium capitalize">
                  {product.marque}
                </span>
              </div>
            </div>

            {/* Sélecteur de couleur */}

            {product?.variants.length > 0 ? (
              <>
                <div className="flex py-2 p-2 items-center">
                  {/* <p className="text-xs font-bold mr-1">Variante:</p> */}
                  <p className="text-xs font-bold mr-1">
                    {selectedVariant?.color}
                  </p>
                </div>

                <div className="flex space-x-2 p-2">
                  {product?.variants.map((variant, index) => (
                    <div
                      className={`w-[70px] h-[70px] rounded-md overflow-hidden transition-all duration-200 
          ${
            selectedVariant?.color === variant?.color
              ? "border-2 border-solid border-[#30A08B]"
              : "border border-gray-300"
          }`}
                      key={index}
                      onClick={() => handleColorSelect(variant.color)}
                    >
                      {variant.imageUrl ? (
                        <img
                          className="w-full h-full object-cover cursor-pointer"
                          src={variant.imageUrl}
                          alt={`Image ${variant.color}`}
                        />
                      ) : (
                        <div
                          className="w-full h-full cursor-pointer"
                          style={{
                            backgroundColor: variant.colorCode || variant.color,
                          }}
                          title={variant.color}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {/* Sélecteur de taille */}
            {availableSizes.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Taille: {selectedSize}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {availableSizes.map((size) => (
                    <button
                      key={size}
                      className={`px-3 py-2 border rounded-md text-sm font-medium ${
                        selectedSize === size
                          ? "border-orange-500 bg-orange-50 text-orange-500"
                          : "border-gray-200 text-gray-900 hover:bg-gray-50"
                      }`}
                      style={{
                        borderColor:
                          selectedSize === size ? COLORS.orange : undefined,
                        backgroundColor:
                          selectedSize === size
                            ? COLORS.lightOrange
                            : undefined,
                        color:
                          selectedSize === size ? COLORS.orange : undefined,
                      }}
                      onClick={() => handleSizeSelect(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Contrôle de quantité + Boutons d'action */}
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-4">
                {/* Sélecteur de quantité */}
                <div className="flex items-center border-2 border-gray-300 rounded-lg overflow-hidden hover:border-orange-500 transition-colors">
                  <button
                    className="px-4 py-3 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={decrementQuantity}
                    disabled={quantity <= 1}
                    title="Diminuer la quantité"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <input
                    type="text"
                    value={quantity}
                    onChange={handleQuantityChange}
                    onBlur={handleQuantityBlur}
                    className="px-4 py-3 text-gray-900 min-w-[60px] text-center font-semibold border-x-2 border-gray-300 focus:outline-none focus:border-orange-500 focus:bg-orange-50"
                    style={{ fontSize: '16px' }}
                  />
                  <button
                    className="px-4 py-3 text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    onClick={incrementQuantity}
                    title="Augmenter la quantité"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>

                {/* Stock disponible */}
                {/* <div className="text-sm text-gray-500">
                  {selectedVariant && (
                    <span>
                      {selectedVariant.stock > 0
                        ? `${selectedVariant.stock} unités disponibles`
                        : "Rupture de stock"}
                    </span>
                  )}
                </div> */}
              </div>

              {/* Boutons d'action */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                  // onClick={addToCart}
                  onClick={() => addToCart(false)}
                  className="py-3 font-medium rounded-md flex-1 flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: COLORS.orange,
                    color: COLORS.white,
                  }}
                  disabled={!product.isdisponible}
                >
                  <ShoppingCart className="h-5 w-5" />
                  Ajouter au panier
                </Button>

                <Button
                  className="py-3 font-medium rounded-md flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => addToCart(true)}
                  style={{
                    backgroundColor: COLORS.green,
                    color: COLORS.white,
                  }}
                  disabled={!product.isdisponible}
                >
                  Acheter maintenant
                </Button>
              </div>
            </div>

            {/* Infos de livraison */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <h3 className="text-sm font-medium">Livraison</h3>

              {product.shipping && product.shipping.zones && (
                <div className="space-y-2">
                  {product.shipping.zones.map((zone) => (
                    <div
                      key={zone._id}
                      className="flex items-start gap-3 text-sm"
                    >
                      <Truck className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium">
                          {zone.name} via {zone.transporteurName}
                        </p>
                        <p className="text-gray-500">
                          Frais: {zone.baseFee.toLocaleString()} €
                          {zone.weightFee > 0
                            ? ` + ${zone.weightFee} €/kg`
                            : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-start gap-3 text-sm">
                <Shield className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Engagement qualité</p>
                  <p className="text-gray-500">
                    Votre satisfaction est au cœur de nos priorités.
                  </p>
                </div>
              </div>
            </div>

            <div style={{ display: "none" }}>
              {product && (
                <Reviews
                  productId={product._id}
                  userName={userName}
                  setRate={setRate}
                  setCountRate={setCountRate}
                />
              )}
            </div>
            <ShareButtons product={product} />
          </div>
        </div>

        {/* Description et détails */}
        <div className="mb-12">
          <Tabs defaultValue="description">
            <TabsList className="w-full bg-gray-100 p-0 mb-6">
              <TabsTrigger
                value="description"
                className="flex-1 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none"
                style={{
                  color: "inherit",
                  borderBottomWidth: "2px",
                  borderBottomColor: "transparent",
                  borderBottomStyle: "solid",
                  ["--tw-border-opacity"]: "1",
                  ["&[data-state=active]"]: {
                    borderBottomColor: COLORS.orange,
                  },
                }}
              >
                Description
              </TabsTrigger>
              <TabsTrigger
                value="specifications"
                className="flex-1 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none"
                style={{
                  color: "inherit",
                  borderBottomWidth: "2px",
                  borderBottomColor: "transparent",
                  borderBottomStyle: "solid",
                  ["&[data-state=active]"]: {
                    borderBottomColor: COLORS.orange,
                  },
                }}
              >
                Spécifications
              </TabsTrigger>
              <TabsTrigger
                value="reviews"
                className="flex-1 py-3 data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-none"
                style={{
                  color: "inherit",
                  borderBottomWidth: "2px",
                  borderBottomColor: "transparent",
                  borderBottomStyle: "solid",
                  ["&[data-state=active]"]: {
                    borderBottomColor: COLORS.orange,
                  },
                }}
              >
                Avis ({rates})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="description" className="mt-0">
              <div className="prose prose-sm sm:prose max-w-none">
                {/* <div>{extractTextFromHTML(product.description)}</div> */}
                <div
                          className="prose max-w-none bg-white p-4 rounded-lg shadow-sm"
                          style={{
                            border: `1px solid ${COLORS.lightGray}`,
                            color: COLORS.gray,
                          }}
                          dangerouslySetInnerHTML={{
                            __html:
                              product.description ||
                              "<p>Aucune description disponible</p>",
                          }}
                        />
              </div>
            </TabsContent>

            <TabsContent value="specifications" className="mt-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 bg-gray-50 w-1/3">
                        Marque
                      </th>
                      <td className="py-3 px-4 text-sm text-gray-900 capitalize">
                        {product.marque}
                      </td>
                    </tr>
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 bg-gray-50">
                        Poids
                      </th>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {product.shipping?.weight
                          ? `${product.shipping.weight} kg`
                          : "Non spécifié"}
                      </td>
                    </tr>
                    {/* <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 bg-gray-50">
                        Dimensions
                      </th>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {product.shipping?.dimensions
                          ? `${product.shipping.dimensions.length} × ${product.shipping.dimensions.width} × ${product.shipping.dimensions.height} cm`
                          : "Non spécifié"}
                      </td>
                    </tr> */}
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 bg-gray-50">
                        Couleurs disponibles
                      </th>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {product.variants.map((v) => v.color).join(", ")}
                      </td>
                    </tr>
                    <tr>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-500 bg-gray-50">
                        Origine
                      </th>
                      <td className="py-3 px-4 text-sm text-gray-900">
                        {product.shipping?.origine || "Non spécifié"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="reviews" className="mt-0">
              {product && (
                <Reviews
                  productId={product._id}
                  userName={userName}
                  setRate={setRate}
                  setCountRate={setCountRate}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Produits similaires */}
        <div className="mb-12">
          <h2 className="text-xl font-bold mb-6">Produits similaires</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {products?.map((product, index) => (
              <div
                key={product._id}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:scale-105"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    onClick={() => navigate(`/ProduitDétail/${product?._id}`)}
                    src={product?.image1 || product?.image}
                    alt={product?.name}
                    className="w-full h-full object-cover"
                  />
                  {/* Badge de promotion si applicable */}
                  {product.prixPromo &&
                    Number(product.prixPromo) > 0 &&
                    Number(product.prixPromo) < Number(product.prix) && (
                      <div
                        className="absolute top-2 left-2 px-2 py-1 rounded-full text-white text-xs font-bold"
                        style={{ backgroundColor: COLORS.orange }}
                      >
                        -
                        {calculateDiscount(
                          Number(product.prix),
                          Number(product.prixPromo)
                        )}
                        %
                      </div>
                    )}

                  <button
                    onClick={(e) => handleLikeClick(product, e)}
                    className={cn(
                      "absolute top-3 left-3 p-2 rounded-full shadow-lg transition-all duration-300 z-20",
                      likedProducts.includes(product._id)
                        ? "bg-red-50 hover:bg-red-100"
                        : "bg-white hover:bg-emerald-50"
                    )}
                  >
                    <Heart
                      className={cn(
                        "w-5 h-5 transition-colors duration-300",
                        likedProducts.includes(product._id)
                          ? "text-red-500 fill-red-500"
                          : "text-emerald-600"
                      )}
                    />
                  </button>
                </div>
                <div className="p-4">
                  <span
                    className="text-xs font-medium px-2 py-1 rounded-full text-white inline-block mb-2"
                    style={{ backgroundColor: COLORS.green }}
                  >
                    {DATA_Categories?.find(
                      (cat) => cat._id === product.ClefCategorie
                    )?.name || "Catégorie"}
                  </span>
                  <h3 className="font-bold text-lg mb-1">{product?.name}</h3>
                  <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                    {/* Essaie d'extraire le texte brut de la description HTML si nécessaire */}
                    {extractTextFromHTML(product.description)}
                  </p>
                  <div className="flex justify-between items-center">
                    {/* Affichage du prix avec réduction si applicable */}
                    <div>
                      {product.prixPromo &&
                      Number(product.prixPromo) > 0 &&
                      Number(product.prixPromo) < Number(product.prix) ? (
                        <div>
                          <span
                            className="font-bold text-lg mr-2"
                            style={{ color: COLORS.orange }}
                          >
                            {Number(product.prixPromo).toLocaleString()} €
                          </span>
                          <span className="text-gray-500 text-sm line-through">
                            {Number(product.prix).toLocaleString()} €
                          </span>
                        </div>
                      ) : (
                        <span
                          className="font-bold text-lg"
                          style={{ color: COLORS.orange }}
                        >
                          {Number(product.prix).toLocaleString()} €
                        </span>
                      )}
                    </div>
                    <button
                      className="px-3 py-1 rounded-full text-sm text-white font-medium flex items-center"
                      style={{ backgroundColor: COLORS.green }}
                      onClick={() => navigate(`/ProduitDétail/${product?._id}`)}
                    >
                      <ShoppingCart size={16} className="mr-1" />
                      Ajouter
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Barre d'action fixe sur mobile */}
      {/* <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-10">
        <div className="flex items-center justify-between gap-3">
          <div className="text-center">
            <button className="p-2 rounded-full hover:bg-gray-100 flex flex-col items-center">
              <Heart
                className={`h-5 w-5 ${
                  isWishlisted ? "fill-red-500 text-red-500" : "text-gray-500"
                }`}
              />
              <span className="text-xs mt-1">Favoris</span>
            </button>
          </div>
          <Button
            onClick={addToCart}
            className="flex-1 py-2 text-sm font-medium"
            style={{
              backgroundColor: COLORS.orange,
              color: COLORS.white,
            }}
            // disabled={!selectedVariant || selectedVariant.stock < 1}
          >
            <ShoppingCart className="h-4 w-4 mr-1" />
            Ajouter au panier
          </Button>
          <Button
            className="flex-1 py-2 text-sm font-medium"
            style={{
              backgroundColor: COLORS.green,
              color: COLORS.white,
            }}
          >
            Acheter maintenant
          </Button>
        </div>
      </div> */}
    </div>
  );
};

// Exemple d'utilisation avec des données de test
const ProductDetailPageWrapper = () => {
  const params = useParams();
  const DATA_Products = useSelector((state) => state.products.data);
  const produit = DATA_Products.find((item) => item._id === params.id);

  const filteredProducts = DATA_Products.filter(
    (item) =>
      item?.ClefCategorie ===
      DATA_Products.find((item) => item._id === params.id)?.ClefCategorie
  );
  // Obtenir les éléments aléatoires à partir du tableau filtré
  const getRandomElements = (array, nbr) => {
    const shuffledArray = shuffle(array);
    return shuffledArray.slice(0, nbr);
  };

  return (
    <ProductDetailPage
      product={produit}
      products={getRandomElements(filteredProducts, 4)}
    />
  );
};

export default ProductDetailPageWrapper;
